import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { Bell, Sparkle, TrendUp } from "@phosphor-icons/react";
import { useTranslation } from "react-i18next";
import api from "@/lib/api";
import { fdatetime } from "@/lib/format";

export default function NotificationsBell() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const { data: countData } = useQuery({
    queryKey: ["alerts-count"],
    queryFn: async () => (await api.get("/matching/alerts/count")).data,
    refetchInterval: 60000,
  });
  const { data: alerts = [] } = useQuery({
    queryKey: ["alerts-list"],
    queryFn: async () => (await api.get("/matching/alerts")).data,
    enabled: open,
  });
  const unread = countData?.unread || 0;

  const readOne = useMutation({
    mutationFn: async (id) => (await api.post(`/matching/alerts/${id}/read`)).data,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["alerts-count"] }); qc.invalidateQueries({ queryKey: ["alerts-list"] }); },
  });
  const readAll = useMutation({
    mutationFn: async () => (await api.post("/matching/alerts/read-all")).data,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["alerts-count"] }); qc.invalidateQueries({ queryKey: ["alerts-list"] }); },
  });

  const openAlert = (a) => {
    if (!a.read) readOne.mutate(a.id);
    setOpen(false);
    if (a.property_id) navigate(`/properties/${a.property_id}`);
  };

  return (
    <div className="relative">
      <button onClick={() => setOpen(!open)} data-testid="notifications-bell"
        className="relative w-full flex items-center gap-2 px-4 py-2.5 text-sm text-slate-300 hover:bg-white/10 hover:text-white transition-colors rounded-sm">
        <Bell size={18} weight={unread ? "fill" : "regular"} className={unread ? "text-[#C5A059]" : ""} />
        {t("matching.alerts", "Alerts")}
        {unread > 0 && (
          <span data-testid="alerts-badge" className="ml-auto min-w-[20px] h-5 px-1.5 rounded-full bg-[#C5A059] text-white text-[11px] font-semibold flex items-center justify-center">{unread}</span>
        )}
      </button>
      {open && (
        <div data-testid="alerts-panel" className="absolute bottom-full left-0 mb-2 w-80 max-h-96 overflow-y-auto bg-white text-slate-800 border border-slate-200 rounded-sm shadow-2xl z-50">
          <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100 sticky top-0 bg-white">
            <span className="font-serif text-sm text-slate-900">{t("matching.newMatches", "New Matches")}</span>
            {alerts.some((a) => !a.read) && (
              <button data-testid="alerts-read-all" onClick={() => readAll.mutate()} className="text-xs text-[#C5A059] hover:underline">{t("matching.markAllRead", "Mark all read")}</button>
            )}
          </div>
          {alerts.length === 0 ? (
            <div className="px-4 py-8 text-center text-sm text-slate-400">{t("matching.noAlerts", "No new alerts")}</div>
          ) : (
            alerts.map((a) => (
              <button key={a.id} onClick={() => openAlert(a)} data-testid={`alert-${a.id}`}
                className={`w-full text-left px-4 py-3 border-b border-slate-50 hover:bg-slate-50 flex gap-3 ${a.read ? "opacity-60" : ""}`}>
                <span className="mt-0.5 flex-shrink-0">{a.type === "match_improved" ? <TrendUp size={16} className="text-emerald-600" /> : <Sparkle size={16} weight="fill" className="text-[#C5A059]" />}</span>
                <span className="min-w-0">
                  <span className="block text-xs font-medium text-slate-800">{t(`matching.alertType.${a.type}`, a.type)}</span>
                  <span className="block text-xs text-slate-600 truncate">{a.party_name} · {a.property_title}</span>
                  <span className="block text-[10px] text-slate-400">{a.score}% · {fdatetime(a.created_at)}</span>
                </span>
                {!a.read && <span className="ml-auto w-2 h-2 rounded-full bg-[#C5A059] flex-shrink-0 mt-1" />}
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
}
